import React, { useMemo, useState } from 'react';
import { parseSovereignHtml } from '../utils/sovereignReportParser';
import { buildSovereignCaseFileHtml } from '../utils/sovereignReportHtml';

function PlaybookRunSheet({ steps }) {
  const [openIndex, setOpenIndex] = useState(0);
  if (!steps?.length) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-4">
        <p className="text-sm text-amber-300 font-medium mb-1">No playbook steps available</p>
        <p className="text-xs text-gray-400">
          This pretext is missing a Playbook Run-Sheet or Elicitation Pathway steps.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="text-sm font-semibold text-legend-teal-dark uppercase tracking-wider mb-3">
        Playbook Run-Sheet
      </h3>
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const open = openIndex === idx;
          return (
            <div
              key={`${step.step_number}-${idx}`}
              className="rounded-lg border border-legend-teal/25 bg-white overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(open ? null : idx)}
                className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-legend-paper transition-colors"
              >
                <span className="text-legend-teal-dark font-bold text-sm mt-0.5 shrink-0">
                  {step.step_number || idx + 1}
                </span>
                <p className="flex-1 text-legend-navy text-sm font-medium leading-relaxed">
                  {step.what_you_do}
                </p>
              </button>
              {open && (
                <div className="border-t border-legend-teal/20 px-4 py-3 space-y-3 bg-legend-paper">
                  {step.what_you_say && (
                    <div>
                        <p className="text-xs font-semibold text-legend-slate uppercase tracking-wider mb-1">
                          Say
                        </p>
                      <p className="text-sm text-legend-navy leading-relaxed whitespace-pre-wrap">
                        {step.what_you_say}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldCard({ row }) {
  if (row.heading === 'Profile') {
    return (
      <div className="border border-legend-teal/25 rounded-lg bg-white p-4 md:p-5">
        <div className="text-xs uppercase tracking-wide text-legend-slate mb-2">DISC rank</div>
        <div className="text-3xl md:text-4xl font-bold text-legend-teal-dark mb-3">{row.content}</div>
        {row.source && <div className="text-sm text-legend-slate">Source: {row.source}</div>}
      </div>
    );
  }
  return (
    <div className="border border-legend-teal/25 rounded-lg bg-white p-4 md:p-5">
      {row.heading ? (
        <div className="font-semibold text-legend-navy text-base md:text-lg mb-2 leading-snug">
          {row.heading}
        </div>
      ) : null}
      <div className="text-legend-navy text-base leading-relaxed whitespace-pre-wrap">{row.content}</div>
      {row.source && <div className="text-sm text-legend-slate mt-3">Source: {row.source}</div>}
    </div>
  );
}

function TargetTable({ targets }) {
  if (!targets?.length) return null;
  return (
    <div className="overflow-x-auto border border-legend-teal/25 rounded-lg bg-white">
      <table className="w-full text-sm text-left">
        <thead className="bg-legend-teal/10 text-legend-navy">
          <tr>
            <th className="px-3 py-2 font-semibold">#</th>
            <th className="px-3 py-2 font-semibold">Name</th>
            <th className="px-3 py-2 font-semibold">Role</th>
            <th className="px-3 py-2 font-semibold">Relationship</th>
            <th className="px-3 py-2 font-semibold">Elicitation angle</th>
          </tr>
        </thead>
        <tbody>
          {targets.map((target) => (
            <tr key={`${target.num}-${target.name}`} className="border-t border-legend-teal/15 align-top">
              <td className="px-3 py-2 text-legend-slate">{target.num}</td>
              <td className="px-3 py-2 text-legend-navy font-medium">{target.name}</td>
              <td className="px-3 py-2 text-legend-navy">{target.role}</td>
              <td className="px-3 py-2 text-legend-navy">{target.relationship}</td>
              <td className="px-3 py-2 text-legend-navy">{target.angle}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SovereignReportViewer({
  htmlContent,
  reportTitle,
  className = '',
  onDownloadHtml,
  savedTier1Draft,
  onTier1DraftChange,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showOriginal, setShowOriginal] = useState(false);
  const parsed = useMemo(() => (htmlContent ? parseSovereignHtml(htmlContent) : null), [htmlContent]);
  const notes = savedTier1Draft?.investigator_notes || '';
  const downloadCaseFile = () => {
    if (!parsed?.sections?.length) return;
    const html = buildSovereignCaseFileHtml(parsed, reportTitle, {
      investigatorNotes: notes,
      sectionNotes: savedTier1Draft?.section_notes || {},
    });
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${String(reportTitle || 'Sovereign_Assessment').replace(/[^\w-]+/g, '_')}_CaseFile.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (!htmlContent) {
    return (
      <div className={`flex items-center justify-center h-full min-h-[28rem] text-gray-500 text-sm ${className}`}>
        <p>No report generated yet.</p>
      </div>
    );
  }

  if (!parsed || parsed.sections.length === 0) {
    return (
      <div className={`h-full min-h-[28rem] ${className}`}>
        <iframe
          title={reportTitle || 'Sovereign Report'}
          className="w-full h-full bg-white"
          srcDoc={htmlContent}
          sandbox="allow-same-origin"
        />
      </div>
    );
  }

  const section = parsed.sections[activeIndex] || parsed.sections[0];
  const isTargetTab = Boolean(section.targets?.length);
  const isDiscTab = /disc|communication profile/i.test(section.title);

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`}>
      <div className="shrink-0 border-b border-legend-gold/40 bg-legend-navy px-3 sm:px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-legend-paper/80 truncate">{reportTitle || 'Sovereign Report'}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowOriginal((value) => !value)}
            className="px-2 sm:px-3 py-1.5 text-xs border border-legend-teal/30 text-legend-paper/80 rounded-md hover:border-legend-teal"
          >
            {showOriginal ? 'Structured view' : 'Original HTML'}
          </button>
          <button
            type="button"
            onClick={downloadCaseFile}
            className="px-2 sm:px-3 py-1.5 text-xs border border-legend-teal/40 text-legend-teal-tint rounded-md hover:bg-legend-teal/10"
            title="Download structured case file"
          >
            Download Case File
          </button>
          {onDownloadHtml && (
            <button
              type="button"
              onClick={onDownloadHtml}
              className="px-2 sm:px-3 py-1.5 text-xs border border-legend-teal/30 text-legend-paper/80 rounded-md hover:border-legend-teal"
              title="Download original HTML"
            >
              Original HTML
            </button>
          )}
        </div>
      </div>
      {showOriginal ? (
        <iframe
          title={reportTitle || 'Sovereign Report'}
          className="flex-1 min-h-[28rem] w-full bg-white"
          srcDoc={htmlContent}
          sandbox="allow-same-origin"
        />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <div className="lg:w-64 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-legend-teal/25 bg-legend-navy">
            <div className="p-2 lg:p-3">
              <p className="text-xs font-semibold text-legend-slate uppercase tracking-wider mb-1.5 lg:mb-2 px-1">
                Sections
              </p>
              <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible gap-1.5 lg:gap-1 pb-2 lg:pb-0 -mx-2 px-2 lg:mx-0 lg:px-0">
                {parsed.sections.map((item, idx) => (
                  <button
                    key={`${item.title}-${idx}`}
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    className={`shrink-0 lg:w-full text-left px-2.5 py-1.5 lg:px-3 lg:py-2 rounded text-xs lg:text-sm transition-colors whitespace-nowrap lg:whitespace-normal ${
                      activeIndex === idx
                        ? 'bg-legend-teal/20 text-legend-teal-tint border border-legend-teal/50 font-medium'
                        : 'bg-legend-deep text-legend-paper/70 border border-transparent hover:text-legend-teal-tint hover:bg-legend-navy'
                    }`}
                  >
                    <div className="leading-tight">{item.title}</div>
                    {item.rowCount > 0 && (
                      <div className="hidden lg:block text-xs text-gray-500 mt-0.5">
                        {item.rowCount} item{item.rowCount === 1 ? '' : 's'}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0 bg-legend-paper">
            <div className="p-3 md:p-6 lg:max-w-4xl">
              <h2 className="text-xl md:text-2xl font-semibold text-legend-navy mb-4 md:mb-6">{section.title}</h2>
              {section.kind === 'prose' && (
                <div className="border border-legend-teal/25 rounded-lg bg-white p-4 md:p-6 space-y-4">
                  {(section.paragraphs || []).map((paragraph, idx) => (
                    <p key={idx} className="text-legend-navy text-base leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
              {section.kind === 'file-list' && (
                <div className="border border-legend-teal/25 rounded-lg bg-white p-4 md:p-6">
                  {(section.files || []).length ? (
                    <ul className="list-disc pl-5 space-y-1 text-legend-navy text-base">
                      {(section.files || []).map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-legend-slate text-base">No files were provided for this assessment.</p>
                  )}
                </div>
              )}
              {isTargetTab && <div className="mb-4">{<TargetTable targets={section.targets} />}</div>}
              {section.kind !== 'prose' && section.kind !== 'file-list' && section.rows?.length > 0 && (
                <div className={`space-y-4 md:space-y-3 ${isTargetTab ? 'mt-4' : ''}`}>
                  {section.rows
                    .filter((row) => !(isTargetTab && section.targets?.length))
                    .map((row, idx) => (
                      <FieldCard key={`${row.heading}-${idx}`} row={row} />
                    ))}
                  {isDiscTab &&
                    !section.rows.some((row) => /communication tips|how to communicate|how to use/i.test(row.heading)) && (
                      <div className="border border-amber-500/30 rounded-lg bg-amber-950/20 p-4 text-sm text-amber-200">
                        DISC usage belongs on this tab. Regenerate if Communication tips is missing.
                      </div>
                    )}
                </div>
              )}
              {section.kind !== 'prose' &&
                section.kind !== 'file-list' &&
                !section.rows?.length &&
                !section.playbook?.length &&
                !isTargetTab && (
                  <div className="text-base text-gray-500 italic py-8 text-center">No structured data available</div>
                )}
              {section.playbook && section.playbook.length > 0 && (
                <div className="mt-6 md:mt-8">
                  <div className="border border-legend-teal/25 rounded-lg bg-white p-4 md:p-6">
                    <PlaybookRunSheet steps={section.playbook} />
                  </div>
                </div>
              )}
              {onTier1DraftChange && (
                <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-legend-teal/20">
                  <h3 className="text-lg md:text-xl font-semibold text-legend-navy mb-4 md:mb-6">
                    Investigator Notes
                  </h3>
                  <textarea
                    defaultValue={notes}
                    onChange={(event) =>
                      onTier1DraftChange({
                        ...(savedTier1Draft || {}),
                        investigator_notes: event.target.value,
                      })
                    }
                    placeholder="Case observations, follow-ups, red flags..."
                    className="w-full px-4 py-3 bg-white border border-legend-teal/25 rounded-lg text-legend-navy text-base leading-relaxed placeholder-legend-slate focus:outline-none min-h-[6rem] md:min-h-[8rem] resize-y"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
